import type { CSSProperties, ReactNode } from 'react';
import { useReveal } from '../hooks/useReveal';
import { cx } from '../cx';
import ui from '../ui.module.css';

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Stagger, in ms. Applied as a transition delay, so reduced motion kills it. */
  delay?: number;
  style?: CSSProperties;
}

/**
 * A block that lifts into place once, without fanfare — 14px and no scale.
 * Renders a plain `<div>`; use `useReveal` directly when the element needs to
 * be a `<section>` or an `<li>` for the document outline to stay correct.
 */
export function Reveal({ children, className, delay = 0, style }: RevealProps) {
  const { ref, state } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      data-reveal={state}
      className={cx(ui.reveal, className)}
      style={delay ? { ...style, transitionDelay: `${delay}ms` } : style}
    >
      {children}
    </div>
  );
}

interface WipeProps {
  /** One entry per printed line. Lines wipe up from their own baseline in turn. */
  lines: ReactNode[];
  className?: string;
  delay?: number;
  /** Gap between lines, in ms. */
  stagger?: number;
}

/**
 * Type that wipes in line by line, as if it were being printed rather than
 * animated: each line is masked by its own box and slides up from below the
 * baseline. Nothing fades, nothing scales, nothing overshoots.
 */
export function Wipe({ lines, className, delay = 0, stagger = 110 }: WipeProps) {
  const { ref, state } = useReveal<HTMLSpanElement>({ threshold: 0.08 });
  return (
    <span ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className={ui.wipe} data-reveal={state}>
          <span style={{ transitionDelay: `${delay + i * stagger}ms` }}>{line}</span>
        </span>
      ))}
    </span>
  );
}
